// ─────────────────────────────────────────────────────────────────────────────
// deltaQueue.ts — THE QUEUE THE OWNER STACKS. HIS ORDER, HELD AS A NUMBER.
//
// OWNER RULING: "Tick boxes on the dropdown, in order — do this one first, this
// one second. All 136 if he wants. DELTA WORKS THE QUEUE AND RETURNS TO IDLE
// BETWEEN COMPANIES."
//
// ── POSITION IS AN EXPLICIT INTEGER, NEVER A DERIVED ORDER ─────────────────
// "This one first, this one second" is a FACT THE OWNER STATES. Array order is
// a second copy of that fact, and the copy drifts the moment anything re-sorts,
// filters or re-renders — a producer's own order surviving a round trip is not
// something a list can promise. So the position is stored, and the array is
// derived FROM it rather than the other way round.
//
// POSITIONS ARE NORMALISED TO 1..n ON EVERY MUTATION. Not cosmetic: two entries
// sharing a position is two companies claiming the same place in his order, and
// there is no correct way to break that tie. Normalising means the tie can never
// exist to be broken.
//
// ── THE STATE IS THE PRODUCER'S WORD, NOT A DERIVATION ─────────────────────
//   'queued' · 'running' · 'done' · 'failed'
// NOT `!running && depth > 0`. When a render is wrong because the producer has
// no word for the state, the word goes upstream — a downstream derivation is a
// second copy of the producer's rule, and it goes stale the same way.
//
// ── AN ENTRY HOLDS AN ID AND NOTHING ELSE ──────────────────────────────────
// No name, no phone, no sector. Everything else is read from the handoff
// payload at render, so a queue cannot go stale against Buck's store: if he
// re-runs contact routes and a number changes, the queue is already right.
// ─────────────────────────────────────────────────────────────────────────────

export type QueueEntryState = 'queued' | 'running' | 'done' | 'failed';

export interface QueueEntry {
  companyId: string;
  /** 1-based, unique, and the AUTHORITY on order. */
  position: number;
  state: QueueEntryState;
}

/** Positions renumbered 1..n in their current order, so no two entries can
 *  claim the same place. Called by every mutation below; never optional. */
function normalise(entries: QueueEntry[]): QueueEntry[] {
  return entries
    .slice()
    .sort((a, b) => a.position - b.position)
    .map((e, i) => (e.position === i + 1 ? e : { ...e, position: i + 1 }));
}

export function orderedQueue(entries: QueueEntry[]): QueueEntry[] {
  return normalise(entries || []);
}

/** Tick a company on. Appended at the end of HIS order — a new tick is the next
 *  one he wants, not a re-sort of the ones he already placed. A second tick of
 *  the same company is a no-op, never a duplicate: one company, one place. */
export function enqueue(entries: QueueEntry[], companyId: string): QueueEntry[] {
  const id = String(companyId || '').trim();
  if (!id) return normalise(entries || []);
  const list = normalise(entries || []);
  if (list.some((e) => e.companyId === id)) return list;
  return normalise([...list, { companyId: id, position: list.length + 1, state: 'queued' }]);
}

/** Untick. The remaining positions close up — his order is preserved, the gap
 *  is not, because a gap is a position nothing occupies. */
export function dequeue(entries: QueueEntry[], companyId: string): QueueEntry[] {
  return normalise((entries || []).filter((e) => e.companyId !== companyId));
}

/** Move a company to an explicit place in his order. `to` is 1-based and is
 *  clamped into the list rather than refused — dragging past the end is a
 *  gesture meaning "last", not an error. */
export function moveTo(entries: QueueEntry[], companyId: string, to: number): QueueEntry[] {
  const list = normalise(entries || []);
  const from = list.findIndex((e) => e.companyId === companyId);
  if (from < 0) return list;
  const target = Math.max(1, Math.min(list.length, Math.floor(to)));
  const moved = list.slice();
  const [entry] = moved.splice(from, 1);
  moved.splice(target - 1, 0, entry);
  return normalise(moved.map((e, i) => ({ ...e, position: i + 1 })));
}

export function setEntryState(
  entries: QueueEntry[],
  companyId: string,
  state: QueueEntryState,
): QueueEntry[] {
  return normalise(
    (entries || []).map((e) => (e.companyId === companyId ? { ...e, state } : e)),
  );
}

/** The next company Delta would work: the lowest position still `queued`.
 *  `null` when there is none — and `null` is "nothing queued", which is NOT the
 *  same as "we have not read the queue". The caller holds that difference. */
export function nextQueued(entries: QueueEntry[]): QueueEntry | null {
  return normalise(entries || []).find((e) => e.state === 'queued') || null;
}

/** How deep the queue still is. `idle` is reported WITH this number — an idle
 *  runner and an empty queue are different facts and must not paint alike. */
export function queueDepth(entries: QueueEntry[]): number {
  return (entries || []).filter((e) => e.state === 'queued').length;
}

export function isQueued(entries: QueueEntry[], companyId: string): boolean {
  return (entries || []).some((e) => e.companyId === companyId);
}
